import React from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { onEmailNotice, TOLD_REASONS } from '@/lib/emailNotice'
import type { EmailNoticeResult } from '@/lib/sdk-official/resources/email-notices'

interface Toast {
  id: number
  text: string
  tone: 'info' | 'warning'
}

/**
 * F-104: after a publication, what happened to its e-mail notice ("Aviso por
 * e-mail para N pessoas em 10 min", or why it will not go out). Mounted once
 * in the shell; publish forms call `announceEmailNotice(data.emailNotice)`.
 */
export function EmailNoticeToaster() {
  const { t, i18n } = useTranslation('emailNotices')
  const [toasts, setToasts] = React.useState<Toast[]>([])

  React.useEffect(() => onEmailNotice((notice: EmailNoticeResult) => {
    let text = ''
    let tone: Toast['tone'] = 'info'
    if (notice.scheduled) {
      const count = notice.recipientCount ?? 0
      text = t('toast.scheduled', { count, formatted: count.toLocaleString(i18n.language) })
    } else if (notice.reason && (TOLD_REASONS as readonly string[]).includes(notice.reason)) {
      text = t(`toast.reasons.${notice.reason}`)
      tone = notice.reason === 'error' ? 'warning' : 'info'
    }
    if (!text) return
    const id = Date.now() + Math.random()
    setToasts((list) => [...list, { id, text, tone }])
    window.setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 7000)
  }), [t, i18n.language])

  if (!toasts.length) return null
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:items-end" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          data-testid="email-notice-toast"
          className={cn(
            'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border p-4 shadow-lg backdrop-blur-sm',
            toast.tone === 'warning' ? 'border-amber-200 bg-amber-50/95 text-amber-900' : 'border-gray-200 bg-white/95 text-gray-800'
          )}
        >
          <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
          <p className="min-w-0 flex-1 text-sm">{toast.text}</p>
          <button
            type="button"
            aria-label={t('toast.dismiss')}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            onClick={() => setToasts((list) => list.filter((x) => x.id !== toast.id))}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
